const HEIF_FTYP_BRANDS = new Set([
  'heic',
  'heix',
  'heim',
  'heis',
  'hevc',
  'hevx',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
  'heif',
]);

// AVIF도 ISO-BMFF(ftyp) 컨테이너지만 모든 모던 브라우저가 디코드하므로 변환 대상에서 제외한다.
const RENDERABLE_FTYP_BRANDS = new Set(['avif', 'avis']);

const FTYP_HEADER_BYTES = 64;
const MAX_SOURCE_BYTES = 30 * 1_024 * 1_024;
const MAX_OUTPUT_EDGE = 2_048;
const JPEG_QUALITY = 0.85;
const DECODE_TIMEOUT_MS = 15_000;

const readBrand = (bytes: Uint8Array, offset: number) =>
  String.fromCharCode(
    bytes[offset],
    bytes[offset + 1],
    bytes[offset + 2],
    bytes[offset + 3],
  );

/**
 * ISO-BMFF ftyp 박스의 매직 바이트로 HEIF(HEIC 포함) 여부를 판별한다.
 * 확장자·MIME을 신뢰하지 않으므로 `.png`로 잘못 라벨된 HEIF(네이티브 앱 경로)도 잡아낸다.
 */
export const isHeif = async (
  input: File | Blob | ArrayBuffer | Uint8Array,
): Promise<boolean> => {
  let bytes: Uint8Array;

  if (input instanceof Uint8Array) {
    bytes = input;
  } else if (input instanceof ArrayBuffer) {
    bytes = new Uint8Array(input);
  } else {
    bytes = new Uint8Array(
      await input.slice(0, FTYP_HEADER_BYTES).arrayBuffer(),
    );
  }

  if (bytes.length < 12) return false;
  if (readBrand(bytes, 4) !== 'ftyp') return false;

  const majorBrand = readBrand(bytes, 8);
  if (RENDERABLE_FTYP_BRANDS.has(majorBrand)) return false;
  if (HEIF_FTYP_BRANDS.has(majorBrand)) return true;

  const boxSize =
    (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
  const limit = Math.min(boxSize > 0 ? boxSize : bytes.length, bytes.length);

  for (let offset = 16; offset + 4 <= limit; offset += 4) {
    const brand = readBrand(bytes, offset);
    if (RENDERABLE_FTYP_BRANDS.has(brand)) return false;
    if (HEIF_FTYP_BRANDS.has(brand)) return true;
  }

  return false;
};

const withTimeout = <T>(
  task: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(timeoutMessage)),
      timeoutMs,
    );

    task.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/**
 * HEIF File을 JPEG File로 변환한다. 디코더(libheif WASM)는 호출 시점에 동적 import하여
 * 일반 업로드 경로의 번들 비용을 0으로 유지한다.
 */
export const convertHeifToJpeg = async (file: File): Promise<File> => {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('HEIF 파일이 너무 큽니다.');
  }

  const { heicTo } = await import('heic-to/next');

  const bitmap = await withTimeout(
    heicTo({ blob: file, type: 'bitmap' }),
    DECODE_TIMEOUT_MS,
    'HEIF 디코딩 시간이 초과되었습니다.',
  );

  try {
    const longestEdge = Math.max(bitmap.width, bitmap.height);
    const scale =
      longestEdge > MAX_OUTPUT_EDGE ? MAX_OUTPUT_EDGE / longestEdge : 1;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas 컨텍스트를 만들 수 없습니다.');
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) throw new Error('JPEG 인코딩에 실패했습니다.');

    const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'image';

    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
};

/**
 * 업로드 직전 호출. HEIF면 JPEG로 변환해 반환하고, 아니면 원본을 그대로 반환한다.
 * 변환 실패는 throw 되며 호출 측에서 toast 등으로 처리한다.
 */
export const ensureRenderableImage = async (file: File): Promise<File> => {
  if (await isHeif(file)) {
    return convertHeifToJpeg(file);
  }

  return file;
};
